import { useState, useEffect, useCallback } from "react";
import { ControlledSelectOption } from "ui";
import { useAdminRegionV2ControllerGetRegionsForSelectOptions } from "service";
import { useDialogStore } from "../state/dialog-store";
import {
  detectRegionConflicts,
  convertRegionsToSelectOptions,
  generateConflictDialogMessage,
} from "../utils/region-utils";

interface UseRegionManagementProps {
  currentDriverId?: string;
  originalRegionIds?: string[];
  onConfirm: () => void;
}

export const useRegionManagement = ({
  currentDriverId,
  originalRegionIds = [],
  onConfirm,
}: UseRegionManagementProps) => {
  const [regionOptions, setRegionOptions] = useState<ControlledSelectOption[]>(
    [],
  );
  const { setDialog } = useDialogStore();

  const { data: regionsSelectOptions } =
    useAdminRegionV2ControllerGetRegionsForSelectOptions({});

  useEffect(() => {
    if (regionsSelectOptions?.data) {
      const options = convertRegionsToSelectOptions(regionsSelectOptions.data);
      setRegionOptions(options);
    }
  }, [regionsSelectOptions]);

  const checkConflictsAndProceed = useCallback(
    (selectedRegionIds: string[]) => {
      if (!regionsSelectOptions?.data) {
        onConfirm();
        return;
      }

      const conflicts = detectRegionConflicts(
        regionsSelectOptions.data,
        selectedRegionIds,
        currentDriverId,
        originalRegionIds,
      );

      if (conflicts.length > 0) {
        const dialogMessage = generateConflictDialogMessage(conflicts);

        setDialog({
          title: "Change Default Driver",
          description: dialogMessage,
          confirmText: "Change Driver",
          cancelText: "Cancel",
          onConfirm: async (onClose) => {
            onConfirm();
            onClose();
          },
        });
      } else {
        onConfirm();
      }
    },
    [
      regionsSelectOptions?.data,
      currentDriverId,
      originalRegionIds,
      onConfirm,
      setDialog,
    ],
  );

  return {
    regionOptions,
    checkConflictsAndProceed,
    regionsData: regionsSelectOptions?.data || [],
  };
};
